import React, { forwardRef, type ReactElement } from 'react';

import styled from '@mint-lib/styled';
import { CheckboxProps } from '@mui/material/Checkbox';
import FormGroup, { FormGroupProps } from '@mui/material/FormGroup';

import Box from '../Box/Box.jsx';
import FormControl from '../FormControl/FormControl.jsx';
import FormLabel from '../FormLabel/FormLabel.jsx';

enum VariantType {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

export interface CheckboxGroupProps extends FormGroupProps {
  /**
   * Display text over the radio group.
   */
  groupLabel?: string;
  /**
   * The position of the label.
   */
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom';
  /**
   * The value of the initially selected radio button.

   */
  value?: string;
  /**
   * The content of the component.
   */
  children?: React.ReactNode;
  /**
   * The size of the component. small is equivalent to the dense checkbox styling.
   */
  size?: CheckboxProps['size'];
  color?: CheckboxProps['color'];
  /**
   * The name used to reference the value of the control.
   */
  name?: string;
  /**
   */
  onKeyDown: FormGroupProps['onKeyDown'];
  /**
   * display selection controls in a single row.
   */
  row?: boolean;
  /**
   * Change event.
   */
  onChange?: FormGroupProps['onChange'];
  /**
   * If `true`, the checkbox is checked.
   */
  checked?: boolean;
  /**
   * Align variants of the checkboxes can be either vertical or horizontal
   */
  variant?: VariantType;

  /**
   * Icon to display next to the group label.
   */
  icon?: ReactElement | null;
}

const CheckboxGroup = forwardRef((props: CheckboxGroupProps, ref) => {
  return (
    <FormControl component="fieldset">
      <Box sx={{ display: 'flex', gap: 1 }}>
        <FormLabel component="legend">{props.groupLabel}</FormLabel>
        {props.icon}
      </Box>
      <StyledFormGroup variant={props.variant} ref={ref}>
        {props.children}
      </StyledFormGroup>
    </FormControl>
  );
});

export default CheckboxGroup;

const StyledFormGroup = styled(FormGroup)<{
  variant?: VariantType;
}>(({ variant }) => ({
  flexWrap: 'nowrap',
  flexDirection: variant === VariantType.Horizontal ? 'row' : 'column',
}));
